/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 20-05-2026
 *
 * Angular-component voor de template-picker modal.
 * Vervangt de AngularJS templatePickerController + templatepicker.html.
 */
import { Component, inject, OnInit } from '@angular/core';
import { BaseIslandComponent } from '../../base-island.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { NgClass } from '@angular/common';
import type { TemplateKeuze, TemplateSamenvatting } from './template-picker.types';

@Component({
    selector: 'dl-template-picker',
    standalone: true,
    imports: [TranslateModule, NgClass],
    templateUrl: './dl-template-picker.component.html',
    styleUrl: './dl-template-picker.component.scss'
})
export class DlTemplatePickerComponent extends BaseIslandComponent implements OnInit {
    /**
     * De lijst met beschikbare templates en richtmodellen, meegegeven via initialState.
     */
    templates: TemplateSamenvatting[] = [];

    /**
     * Het resultaat dat na sluiten van de modal beschikbaar is voor de aanroeper.
     * null = geannuleerd, { id: false } = nieuw leeg model, TemplateSamenvatting = template gekozen.
     */
    resultaat: TemplateKeuze = null;

    /**
     * De gesplitste lijsten: sjablonen en richtmodellen.
     */
    protected picklist: { templates: TemplateSamenvatting[]; normmodels: TemplateSamenvatting[] } = {
        templates: [],
        normmodels: []
    };

    /**
     * De actieve sublijst: 'templates', 'normmodels' of '-' (niets geselecteerd).
     */
    protected sublist: 'templates' | 'normmodels' | '-' = '-';

    /**
     * Het geselecteerde template, of null als er niets geselecteerd is.
     */
    protected geselecteerd: TemplateSamenvatting | null = null;

    private readonly modalRef = inject(BsModalRef);
    private readonly translateService = inject(TranslateService);

    constructor() { super(); }

    ngOnInit(): void {
        for (const t of this.templates) {
            this.picklist[t.norm ? 'normmodels' : 'templates'].push(t);
        }
    }

    /**
     * Zet de actieve sublijst (sjablonen of richtmodellen).
     */
    protected setSublist(sl: 'templates' | 'normmodels'): void {
        this.sublist = sl;
    }

    /**
     * Klik op een template in de lijst: selecteer, of laad direct bij dubbelklik.
     */
    protected klikTemplate(template: TemplateSamenvatting, laden: boolean): void {
        if (laden) {
            this.resultaat = template;
            this.modalRef.hide();
            return;
        }
        this.geselecteerd = template;
    }

    /**
     * Laad het geselecteerde template.
     */
    protected laadTemplate(): void {
        if (this.geselecteerd) {
            this.resultaat = this.geselecteerd;
            this.modalRef.hide();
        }
    }

    /**
     * Start een nieuw leeg model.
     */
    protected nieuwModel(): void {
        this.resultaat = {
            id: false,
            titel: this.translateService.instant('NEWMODEL')
        };
        this.modalRef.hide();
    }

    /**
     * Sluit de modal zonder keuze (annuleren).
     */
    protected annuleren(): void {
        this.resultaat = null;
        this.modalRef.hide();
    }
}
